import { useEffect, useRef, useState } from "react";
import { formatIDR } from "../../../utils/helpers/formatIDR";
import "../../PurchasePage/FormPurchase/style.css";
import "../PaidRevenueModal/style.css";
import "./style.css";

const number = (value) => Number(value || 0).toLocaleString("id-ID");
const METHOD_LABELS = {
  cash: "Tunai / Cash",
  qris: "QRIS",
  transfer: "Transfer",
  other: "Lainnya / belum terklasifikasi",
};

const CashierSummaryModal = ({ summary, dateRange, loading = false, onRetry, scopeNote = "Mengikuti periode, pencarian, dan status aktif. Transaksi dibatalkan tidak dihitung." }) => {
  const recap = summary?.cashier_summary;
  const products = (recap?.products || []).filter((product) => Number(product.total_qty) > 0);
  const [selectedKey, setSelectedKey] = useState(null);
  const modalRef = useRef(null);
  const backRef = useRef(null);
  const triggerRef = useRef(null);
  const filterKey = JSON.stringify([
    dateRange?.startDate,
    dateRange?.endDate,
    summary?.filters?.status,
    summary?.filters?.search,
  ]);
  const selected = (recap?.cashiers || []).find((row) => String(row.cashier_id ?? "unknown") === selectedKey);

  useEffect(() => {
    setSelectedKey(null);
  }, [filterKey]);

  useEffect(() => {
    const modal = modalRef.current;
    const reset = () => setSelectedKey(null);
    modal?.addEventListener("hidden.bs.modal", reset);
    return () => modal?.removeEventListener("hidden.bs.modal", reset);
  }, []);

  useEffect(() => {
    if (selected) backRef.current?.focus();
  }, [selected]);

  const returnToRecap = (event) => {
    // Prevent Bootstrap's delegated dismiss handler from seeing the recap button
    // after React changes the detail view back during this same click.
    event?.stopPropagation();
    setSelectedKey(null);
    requestAnimationFrame(() => triggerRef.current?.focus());
  };

  useEffect(() => {
    const modal = modalRef.current;
    const beforeHide = (event) => {
      if (!selected) return;
      // Escape/backdrop follow the same back-navigation as the detail's close button.
      event.preventDefault();
      setSelectedKey(null);
      requestAnimationFrame(() => triggerRef.current?.focus());
    };
    modal?.addEventListener("hide.bs.modal", beforeHide);
    return () => modal?.removeEventListener("hide.bs.modal", beforeHide);
  }, [selected]);

  return (
    <div
      ref={modalRef}
      className="modal modal-blur fade sale-paid-revenue-modal sale-cashier-modal"
      id="modal-sale-cashier-summary"
      tabIndex="-1"
      role="dialog"
      aria-modal="true"
      aria-labelledby="sale-cashier-summary-title"
      style={{ backgroundColor: "rgba(0, 0, 0, 0.3)" }}>
      <div className={`modal-dialog modal-dialog-centered${selected ? " is-detail" : ""}`} role="document">
        <div className="modal-content purchase-modal-content">
          <div className="modal-header purchase-modal-header">
            <div>
              <div className="text-secondary small fw-semibold text-uppercase">Ringkasan penjualan</div>
              <h5 className="modal-title mb-0" id="sale-cashier-summary-title">
                {selected ? "Rincian Transaksi Kasir" : "Rekap Penjualan per Kasir"}
              </h5>
            </div>
            <button
              type="button"
              className="btn-close"
              aria-label={selected ? "Kembali ke rekap kasir" : "Tutup rekap kasir"}
              data-bs-dismiss={selected ? undefined : "modal"}
              onClick={selected ? returnToRecap : undefined}
            />
          </div>
          <div className="modal-body purchase-modal-body">
            <div className="sale-paid-revenue-period text-secondary small">
              {dateRange?.startDate || "Awal pencatatan"} – {dateRange?.endDate || "Sekarang"}
            </div>
            {loading ? (
              <div className="sale-paid-revenue-state" role="status">
                <span className="spinner-border spinner-border-sm text-primary" />
                Memuat rekap kasir...
              </div>
            ) : !recap ? (
              <div className="sale-paid-revenue-state" role="status">
                <p className="mb-0">Rekap belum tersedia untuk filter ini.</p>
                <button type="button" className="btn btn-primary btn-sm" onClick={onRetry}>
                  Muat ulang
                </button>
              </div>
            ) : !recap.cashiers?.length ? (
              <p className="text-secondary text-center py-4 mb-0">
                Belum ada transaksi pada periode dan filter tersebut.
              </p>
            ) : (
              <>
                <div hidden={Boolean(selected)}>
                  <div
                    className="table-responsive border rounded-3"
                    tabIndex="0"
                    role="region"
                    aria-label="Tabel rekap penjualan per kasir">
                    <table className="table table-vcenter card-table sale-cashier-table">
                      <thead>
                        <tr>
                          <th scope="col" className="text-center">No</th>
                          <th scope="col">Produk</th>
                          {recap.cashiers.map((cashier, cashierIndex) => (
                            <th
                              scope="col"
                              className={`text-end sale-cashier-column sale-cashier-column--${cashierIndex % 2 ? "neutral" : "tinted"}`}
                              key={cashier.cashier_id ?? "unknown"}>
                              {cashier.cashier_name}
                            </th>
                          ))}
                          <th scope="col" className="text-end">Total Terjual</th>
                        </tr>
                      </thead>
                      <tbody>
                        {products.map((product, index) => (
                          <tr key={product.key}>
                            <td className="text-center text-secondary">{index + 1}</td>
                            <th scope="row" className="fw-semibold sale-cashier-product-name">{product.name}</th>
                            {recap.cashiers.map((cashier, cashierIndex) => (
                              <td
                                className={`text-end sale-cashier-column sale-cashier-column--${cashierIndex % 2 ? "neutral" : "tinted"}`}
                                key={cashier.cashier_id ?? "unknown"}>
                                {number(cashier.products?.[product.key])}
                              </td>
                            ))}
                            <td className="text-end fw-semibold">{number(product.total_qty)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="fw-bold bg-primary-lt">
                          <th scope="row" colSpan="2">Total Produk Terjual</th>
                          {recap.cashiers.map((cashier) => (
                            <td className="text-end" key={cashier.cashier_id ?? "unknown"}>
                              {number(cashier.total_products)}
                            </td>
                          ))}
                          <td className="text-end">{number(recap.total_products)}</td>
                        </tr>
                        <tr className="fw-bold bg-primary-lt">
                          <th scope="row" colSpan="2">Jumlah Transaksi</th>
                          {recap.cashiers.map((cashier) => (
                            <td className="text-end" key={cashier.cashier_id ?? "unknown"}>
                              <button
                                type="button"
                                className="sale-cashier-transaction-link"
                                aria-label={`Lihat detail transaksi ${cashier.cashier_name}`}
                                onClick={(event) => {
                                  triggerRef.current = event.currentTarget;
                                  setSelectedKey(String(cashier.cashier_id ?? "unknown"));
                                }}>
                                {number(cashier.total_transactions)}
                              </button>
                            </td>
                          ))}
                          <td className="text-end">{number(recap.total_transactions)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  <p className="text-secondary small mt-3 mb-0">
                    {scopeNote}
                  </p>
                </div>
                {selected ? (
                  <div>
                    <section className="sale-paid-revenue-total">
                      <span className="fw-semibold">{selected.cashier_name}</span>
                      <strong>{number(selected.total_transactions)} transaksi</strong>
                      <span className="text-secondary small">
                        Total nilai transaksi {formatIDR(selected.total_amount)}
                      </span>
                    </section>
                    <section
                      className="purchase-section sale-paid-revenue-methods"
                      aria-label="Rincian metode pembayaran kasir">
                      <h3 className="fs-4 mb-2">Metode pembayaran</h3>
                      {(selected.methods || [])
                        .filter((method) => method.payment_method !== "other" || method.total_transactions > 0)
                        .map((method) => (
                          <div className="sale-paid-revenue-method" key={method.payment_method}>
                            <div className="sale-paid-revenue-method-label">
                              <div className="fw-semibold">
                                {METHOD_LABELS[method.payment_method] || method.payment_method}
                              </div>
                              <small className="text-secondary">{number(method.total_transactions)} transaksi</small>
                            </div>
                            <strong>{formatIDR(method.amount)}</strong>
                          </div>
                        ))}
                    </section>
                    <p className="text-secondary small mt-3 mb-0">
                      Nominal adalah nilai penjualan, bukan kas diterima. Satu transaksi dengan beberapa metode dihitung
                      sekali pada setiap metode yang digunakan; total transaksi tetap unik.
                    </p>
                  </div>
                ) : null}
              </>
            )}
          </div>
          <div className="modal-footer purchase-modal-footer">
            {selected ? (
              <button ref={backRef} type="button" className="btn btn-primary ms-auto" onClick={returnToRecap}>
                Kembali ke rekap
              </button>
            ) : (
              <button type="button" className="btn btn-primary ms-auto" data-bs-dismiss="modal">
                Tutup
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
export default CashierSummaryModal;
